<template>
  <div class="cpd">
    <router-link to="/portal/projects" class="back-link">← {{ t('portal.navProjects') }}</router-link>

    <div v-if="loading" class="spinner"></div>

    <template v-else-if="project">
      <div class="page-header">
        <div>
          <h1 class="page-title"><span class="dot" :style="{ background: project.color }"></span>{{ project.name }}</h1>
          <p v-if="project.description" class="page-sub">{{ project.description }}</p>
        </div>
      </div>

      <!-- Billing snapshot -->
      <div class="bill-strip">
        <div class="bill"><div class="bv">{{ priceTypeLabel }}</div><div class="bl">{{ t('portal.billingModel') }}</div></div>
        <div class="bill"><div class="bv">{{ hours }}</div><div class="bl">{{ t('portal.hoursTracked') }}</div></div>
        <div class="bill"><div class="bv">{{ total }}</div><div class="bl">{{ t('portal.estBilling') }}</div></div>
      </div>

      <!-- Read-only task list -->
      <div class="section-header"><h2 class="section-title">{{ t('portal.tasks') }}</h2></div>
      <div v-if="tasks.length === 0" class="empty-state"><p>{{ t('portal.noTasks') }}</p></div>
      <div v-else class="task-list">
        <div v-for="tk in tasks" :key="tk.id" class="c-task">
          <div class="c-row" @click="toggle(tk.id)">
            <button class="exp" :class="{ open: expanded[tk.id] }">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <div class="c-main">
              <div class="c-title">{{ tk.title }}</div>
              <div class="c-meta">
                <span v-if="tk.subtask_total">{{ tk.subtask_done }}/{{ tk.subtask_total }} {{ t('subtasks.toggle') }}</span>
              </div>
            </div>
            <TaskTimer
              v-if="tk.timer_started_at || tk.total_seconds"
              :task-id="tk.id" :timer-seconds="tk.timer_seconds" :timer-started-at="tk.timer_started_at" readonly
            />
            <span :class="['badge', 'badge-' + tk.status]">{{ t('status.' + tk.status) }}</span>
          </div>
          <template v-if="expanded[tk.id]">
            <TaskSubtasks :task-id="tk.id" read-only />
            <TaskComments :task-id="tk.id" :can-comment="tk.status !== 'done'" />
          </template>
        </div>
      </div>
    </template>

    <div v-else class="empty-state"><p>{{ t('portal.noProjects') }}</p></div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/api'
import TaskTimer from '@/components/TaskTimer.vue'
import TaskSubtasks from '@/components/TaskSubtasks.vue'
import TaskComments from '@/components/TaskComments.vue'
import { useI18n } from '@/composables/useI18n'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { fmtMoney, projectTotal, projectHours } from '@/utils/billing'

export default {
  name: 'ClientProjectDetail',
  components: { TaskTimer, TaskSubtasks, TaskComments },
  setup() {
    const route = useRoute()
    const { t } = useI18n()
    const pid = Number(route.params.id)
    const project = ref(null)
    const tasks = ref([])
    const loading = ref(true)
    const expanded = reactive({})

    const hours = computed(() => projectHours(project.value) + 'h')
    const total = computed(() => fmtMoney(projectTotal(project.value), project.value?.currency || 'ILS'))
    const priceTypeLabel = computed(() => {
      const pt = project.value?.pricing_type
      return pt === 'fixed' ? t('projects.pricingFixed') : pt === 'hourly' ? t('projects.pricingHourly') : t('portal.noPricing')
    })

    function toggle(id) { expanded[id] = !expanded[id] }

    async function load() {
      loading.value = true
      try {
        const [pr, tr] = await Promise.all([api.getProjects(), api.getTasks({ project_id: pid })])
        project.value = (pr.data || []).find(p => p.id === pid) || null
        tasks.value = tr.data || []
      } catch {} finally { loading.value = false }
    }

    let off = () => {}
    onMounted(() => { load(); off = onRealtime('task.changed', debounce(load, 400)) })
    onUnmounted(() => off())

    return { t, project, tasks, loading, expanded, toggle, hours, total, priceTypeLabel }
  },
}
</script>

<style scoped>
.back-link { font-size: 13px; color: var(--muted); text-decoration: none; display: inline-block; margin-bottom: 14px; }
.back-link:hover { color: var(--accent); }
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.dot { display: inline-block; width: 12px; height: 12px; border-radius: 4px; margin-inline-end: 8px; vertical-align: middle; }

.bill-strip {
  display: grid; grid-template-columns: repeat(3, 1fr);
  border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface);
  overflow: hidden; margin: 18px 0 28px;
}
.bill { padding: 14px 18px; }
.bill + .bill { border-inline-start: 1px solid var(--border); }
.bv { font-size: 22px; font-weight: 600; font-variant-numeric: tabular-nums; }
.bl { font-size: 11px; color: var(--muted); margin-top: 5px; }

.section-header { margin-bottom: 14px; }
.section-title { font-size: 16px; font-weight: 600; }
.task-list { display: flex; flex-direction: column; gap: 8px; }
.c-task { border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface); overflow: hidden; }
.c-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; cursor: pointer; }
.c-row:hover { background: var(--accent-wash); }
.exp { border: none; background: none; color: var(--muted); display: flex; padding: 0; cursor: pointer; }
.exp svg { transition: transform .15s; }
.exp.open svg { transform: rotate(90deg); }
.c-main { flex: 1; min-width: 0; }
.c-title { font-size: 14px; font-weight: 500; }
.c-meta { font-size: 12px; color: var(--muted); margin-top: 2px; }

@media (max-width: 768px) {
  .bill-strip { grid-template-columns: 1fr; }
  .bill + .bill { border-inline-start: none; border-top: 1px solid var(--border); }
}
</style>
